import { Helmet } from 'react-helmet';
import { twc } from 'react-twc';

import { Badge } from '~/modules/elements/akui/badge';
import { Menu } from '~/modules/elements/akui/menu';
import { useBackground } from '~/modules/elements/background-context';
import Logo from '~/modules/elements/logo';
import MenuWithLogo from '~/modules/elements/menu-with-logo';
import useBackgroundMusic from '~/modules/hooks/use-background-music';
import useJukeboxHidden from '~/modules/hooks/use-jukebox-hidden';
import useKeyboardNav, { KeyboardNavContext } from '~/modules/hooks/use-keyboard-nav';
import useSmoothNavigate from '~/modules/hooks/use-smooth-navigate';
import { FeatureFlags } from '~/modules/utils/feature-flags';
import isDesktop from '~/modules/utils/is-desktop';
import useFeatureFlag from '~/modules/utils/use-feature-flag';
import { MAIN_MENU_TITLE } from '~/routes/keyboard-help/screen-titles';
import {
  DesktopSetupCompletedSetting,
  MobilePhoneModeSetting,
  useSettingValue,
} from '~/routes/settings/settings-state';
import DesktopSetup from '~/routes/welcome/desktop-setup';
import LeaderboardPanel from '~/routes/welcome/leaderboard-panel';
import LogoRing from '~/routes/welcome/logo-ring';
import { PrimaryAction, SecondaryAction, UtilityAction } from '~/routes/welcome/menu-actions';
import useMicSetupSummary from '~/routes/welcome/use-mic-setup-summary';
import useSingersSummary from '~/routes/welcome/use-singers-summary';

/**
 * Only the packaged app needs the setup screen - in a browser the room links can just use the
 * current origin, so there is nothing to ask about.
 */
const needsDesktopSetup = (completed: boolean) => isDesktop() && !completed;

function Welcome() {
  useBackground(true);

  const navigate = useSmoothNavigate();
  const onlineModeEnabled = useFeatureFlag(FeatureFlags.OnlineMode);
  // Experiment: hiding the Jukebox entry point to see whether it changes how many songs people sing.
  const jukeboxHidden = useJukeboxHidden();
  const micSetup = useMicSetupSummary();
  const singers = useSingersSummary();

  useBackgroundMusic(true);
  const [mobilePhoneMode] = useSettingValue(MobilePhoneModeSetting);
  const [desktopSetupCompleted, setDesktopSetupCompleted] = useSettingValue(DesktopSetupCompletedSetting);
  const { register } = useKeyboardNav({
    title: MAIN_MENU_TITLE,
    // The top of the tree, and the one entry everything else is an alternative to.
    primary: 'sing-a-song',
    // The menu is the screen most likely to be read from a sofa, so its hints go along the bottom
    // rather than into the corner panel.
    presentation: 'strip',
    // Two columns of entries, so "the next one down" is a question about where things are on screen
    // rather than about the order they registered in.
    direction: 'grid',
    // The setup screen registers its own entries - two active navs would double up on Enter.
    enabled: !needsDesktopSetup(desktopSetupCompleted),
  });

  if (needsDesktopSetup(desktopSetupCompleted)) {
    return <DesktopSetup onDone={() => setDesktopSetupCompleted(true)} />;
  }

  const elsewhere = (onlineModeEnabled ? 1 : 0) + (jukeboxHidden ? 0 : 1);

  return (
    <>
      <MenuWithLogo
        centered
        sidePanel={<LeaderboardPanel />}
        // Not drawn on F5, and staying anyway: it is an attribution obligation. Passed as the
        // footer so it sits inside the viewport-height box rather than pushing past the bottom of it.
        footer={
          <BackgroundMusicCredit>
            <span>
              Bpm data and release year provided by{' '}
              <a target="_blank" href="https://getsongbpm.com/" rel="noreferrer">
                GetSongBPM
              </a>
            </span>
            <span>Song: Funk Cool Groove (Music Today 80)</span>
            <span>• Composed & Produced by : Anwar Amr</span>
            <span>
              • Video Link:{' '}
              <a href="https://youtu.be/FGzzBbYRjFY" target="_blank" rel="noreferrer">
                https://youtu.be/FGzzBbYRjFY
              </a>
            </span>
          </BackgroundMusicCredit>
        }
        // 776px, so the entries themselves come out at the 720 the frame draws once the surface's
        // own padding is taken off. Without it the widest breakpoint stretches them to 900.
        className="gap-3.5 2xl:max-w-[48.5rem]"
        // The wordmark is the centrepiece here and nowhere else, so on a screen with the room for it
        // it grows to the 214px tall F5 draws - which is 1.2x this, not the 1.6x the frame's own
        // markup applies to a much smaller base. It keeps its own `view-transition-name`, so leaving
        // the menu animates it across to wherever it lands next; the ring is decoration behind it.
        logo={
          mobilePhoneMode ? undefined : (
            <LogoRing>
              <div className="mobile:text-5xl tv:text-[12rem] text-6xl">
                <Logo />
              </div>
            </LogoRing>
          )
        }>
        <Helmet>
          <title>Main Menu | AllKaraoke.Party - Free Online Karaoke Party Game</title>
          <link rel="preload" href="/songs/index.json" as="fetch" type="application/json" crossOrigin="anonymous" />
          <link
            rel="preload"
            href="/most-popular-songs.json"
            as="fetch"
            type="application/json"
            crossOrigin="anonymous"
          />
        </Helmet>
        <KeyboardNavContext value={register}>
          <PrimaryAction
            name="sing-a-song"
            remoteLabel="Sing a song"
            remoteIcon="play"
            onClick={() => navigate('game/')}>
            Sing a song
          </PrimaryAction>
          {elsewhere > 0 && (
            <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">
              {onlineModeEnabled && (
                <SecondaryAction
                  name="online"
                  remoteLabel="Sing Online"
                  remoteIcon="play"
                  wide={elsewhere === 1}
                  onClick={() => navigate('online/')}>
                  Sing Online
                  <Badge>Preview</Badge>
                </SecondaryAction>
              )}
              {!jukeboxHidden && (
                <SecondaryAction name="jukebox" wide={elsewhere === 1} onClick={() => navigate('jukebox/')}>
                  Jukebox
                </SecondaryAction>
              )}
            </div>
          )}
          <Menu.Divider className="my-1.5 border-white/10" />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <UtilityAction
              name="select-input"
              remoteLabel="Setup Microphones"
              state={micSetup}
              onClick={() => navigate('select-input/')}>
              Setup Microphones
            </UtilityAction>
            {/* Profiles, History and Stats are one screen (see `singers-page`), so they are one
                entry. `profiles/` of the three routes it answers to, because that is the region with
                a list to open - the other two are readings, and the page draws all three whichever
                way in you take. */}
            <UtilityAction name="singers" state={singers} onClick={() => navigate('profiles/')}>
              Singers
            </UtilityAction>
            <UtilityAction name="manage-songs" onClick={() => navigate('manage-songs/')}>
              Manage Songs
            </UtilityAction>
            <UtilityAction name="settings" remoteIcon="settings" onClick={() => navigate('settings/')}>
              Settings
            </UtilityAction>
          </div>
        </KeyboardNavContext>
        {/* Narrow screens have no second column, so the board sits here — below the menu, above the
            credits. The desktop copy is passed to `MenuWithLogo` as `sidePanel`; SWR dedupes the fetch. */}
        <LeaderboardPanel className="mt-2 lg:hidden" />
      </MenuWithLogo>
    </>
  );
}

const BackgroundMusicCredit = twc.div`typography flex flex-col text-sm no-underline opacity-85 [view-transition-name:background-music-credit]`;

export default Welcome;
